import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as RouteService from '../../services/routes';
import { getSelectedDepot } from '../selectors/app';
import { getRoutesList } from '../selectors/routes';
import { NotificationType } from '../../features/Notifier';
import { DISMISS } from '../../constants/strings';
import RouteStatusDescription from '../../enums/RouteStatusDescription';
import { updateRouteStatus } from './route';
import { getErrorMessage } from '../../utils/errors';
import { showNotification } from '../actions/notifications';

const initialState = {
  routes: {},
  selectedFilters: [],
  routeDetailId: null,
  depotStats: {},
  scrollToRoute: '',
  loading: false,
  error: null,
};

export const changeRouteOpportunityTime = createAsyncThunk(
  'routes/changeRouteOpportunityTime',
  async (
    { opportunityTime, routeId },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const route = getRoutesList(state)[routeId];

      // Update the route with the new opportunity time
      dispatch(
        updateListRoute({
          ...route,
          opportunityTime,
        })
      );
      return { routeId, opportunityTime };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchRoutes = createAsyncThunk(
  'routes/fetchRoutes',
  async ({ depotId, sectionId, date }, { dispatch, rejectWithValue }) => {
    try {
      const data = await RouteService.getRoutes(depotId, sectionId, date);
      dispatch(successRouteFetch(data));
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const changeRouteStatus =
  ({ routeStatus, routeId, isRouteDetail }) =>
  async (dispatch, getState) => {
    const state = getState();
    const depotCode = getSelectedDepot(state);
    const route = getRoutesList(state)[routeId];

    try {
      const { data } = await RouteService.changeRouteStatus(
        depotCode,
        routeStatus,
        routeId
      );
      if (data && data.success) {
        dispatch(
          updateListRoute({
            ...route,
            routeStatus,
            routeStatusDescription: RouteStatusDescription[routeStatus],
          })
        );
        isRouteDetail && dispatch(updateRouteStatus(routeStatus));
      }
      return data;
    } catch (error) {
      dispatch(
        showNotification({
          type: NotificationType.ACTION,
          message: getErrorMessage(error),
          buttonText: DISMISS,
        })
      );
    }
  };

export const updateRouteInList = createAsyncThunk(
  'routes/updateRouteInList',
  async ({ routeId, updatedData }, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const route = getRoutesList(state)[routeId];

      const updatedRoute = {
        ...route,
        ...updatedData,
      };

      dispatch(updateListRoute(updatedRoute));

      return updatedRoute;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const routesSlice = createSlice({
  name: 'routes',
  initialState,
  reducers: {
    updateListRoute(state, action) {
      const { routeId, ...updatedData } = action.payload;
      state.routes[routeId] = {
        ...state.routes[routeId],
        ...updatedData,
      };
    },
    setScrollToRoute(state, action) {
      state.scrollToRoute = action.payload;
    },
    resetScrollToRoute(state) {
      state.scrollToRoute = '';
    },
    successRouteFetch(state, action) {
      const { routes, depotStats } = action.payload;
      state.routes = routes.reduce((acc, route) => {
        acc[route.routeId] = route;
        return acc;
      }, {});
      state.depotStats = depotStats;
    },
  },
});

export const {
  updateListRoute,
  setScrollToRoute,
  resetScrollToRoute,
  successRouteFetch,
} = routesSlice.actions;

export default routesSlice.reducer;
